<template>
  <fieldset id="subscription-options" class="subscription-options">
    <div class="flex-inline between header">
      <strong>Select Frequency:</strong>
      <CustomButton
        class="subscription-learn-more"
        aria-label="Learn More"
        style-name="tertiary"
        @click-button="state.showLearnMore = true"
      >
        Learn More
      </CustomButton>
    </div>
    <ModalWindow v-if="state.showLearnMore" class="modal-learn-more" @closed="closeModal">
      <div ref="modalBody" class="modal-body">
        <h2>Subscribe & Save</h2>
        <ul>
          <li>
            <div class="icon">
              <CalendarEventFill class="calendar" />
            </div>
            <div class="copy">
              <h5>Flexible Frequency</h5>
              <p>You can adjust your delivery frequency anytime in your subscription portal.</p>
            </div>
          </li>
          <li>
            <div class="icon">
              <AlarmLine class="alarm" />
            </div>
            <div class="copy">
              <h5>3 Day Reminder</h5>
              <p>
                We'll email you before each delivery ships. If you're not ready, you can delay it and won't be charged
                until it ships.
              </p>
            </div>
          </li>
          <li>
            <div class="icon">
              <Edit2Line class="edit" />
            </div>
            <div class="copy">
              <h5>Modify and Cancel anytime</h5>
              <p>You can easily modify or cancel your subscription at your convenience.</p>
            </div>
          </li>
        </ul>
      </div>
    </ModalWindow>
    <fieldset :class="{ selected: state.subscriptionType === 'onetime' }">
      <CheckRadio
        name="subscription-type"
        value="onetime"
        type="radio"
        :is-default="true"
        :emit-input-change="'radio-selected'"
        :radio-checked="state.subscriptionType"
        @radio-selected="frequencyChange"
      >
        One-Time Purchase
      </CheckRadio>
      <div v-if="currentPrice" class="price-container">
        <strong>{{ localeStore.currencySymbol }}{{ currentPrice }}</strong>
      </div>
    </fieldset>
    <fieldset :class="[{ selected: state.subscriptionType === 'subscription' }, 'sub-section']">
      <CheckRadio
        name="subscription-type"
        value="subscription"
        type="radio"
        :emit-input-change="'radio-selected'"
        :radio-checked="state.subscriptionType"
        @radio-selected="frequencyChange"
      >
        Subscribe & Save {{ subscriptionSaved }}
      </CheckRadio>
      <div v-if="currentPrice && subscriptionPrice" class="price-container">
        <strong class="percent-off strikethrough">{{ localeStore.currencySymbol }}{{ currentPrice }}</strong>
        <strong>{{ localeStore.currencySymbol }}{{ subscriptionPrice }}</strong>
      </div>
      <transition name="subscription-options">
        <select
          v-if="state.subscriptionType === 'subscription'"
          ref="subscriptionOptions"
          v-model="state.selectedSubscription"
          placeholder="Select a Subscription Option"
          name="subscriptionOptions"
          class="options-container"
        >
          <option v-for="option in sortedSubscriptions" :key="option.id" :value="option" class="option">
            {{ subscriptionDisplay(option) }}
          </option>
        </select>
      </transition>
    </fieldset>
  </fieldset>
</template>
<script setup lang="ts">
import { get } from 'lodash-es';
import { useProductDataStore } from '~/store/product-data';
import { useLocaleStore } from '~/store/locale';

const localeStore = useLocaleStore();

const emit = defineEmits(['selected-sub', 'freq-change']);

// TODO: flagged due to template not being able to tell the difference - fix later
// eslint-disable-next-line vue/no-dupe-keys
const subscriptionOptions = ref<HTMLSelectElement | null>(null);

const props = defineProps({
  subscriptionOptions: {
    type: Array as () => any[],
    required: true,
  },
  sku: {
    type: String,
    required: true,
  },
});

const state = reactive({
  showLearnMore: false,
  subscriptionType: 'onetime',
  selectedSubscription: {} as any,
});

const price = computed(() => useProductDataStore().getPriceBySku(props.sku));
const currentPrice = computed(() => price.value?.calculated_price || 0);

const sortedSubscriptions = computed<any[]>(() => {
  return props.subscriptionOptions
    .filter((option) => option.type === 'subscription')
    .sort((a, b) => {
      return a.sort_order - b.sort_order;
    });
});
const subscriptionDefault = computed<string>(() => {
  return sortedSubscriptions.value?.length > 0 ? sortedSubscriptions.value[0] : '';
});
const subscriptionDiscount = computed<number>(() => {
  const amt = props.subscriptionOptions.length > 0 ? get(props.subscriptionOptions, '[0].discount_amount') : '';
  const percent = amt ? Math.round(parseInt(amt)) : 0;
  return percent;
});
const subscriptionSaved = computed<string>(() => {
  return `${subscriptionDiscount.value}%`;
});
const subscriptionPrice = computed<number>(() => {
  const percent = subscriptionDiscount.value / 100;
  const subscriptionPrice = currentPrice.value - currentPrice.value * percent;
  const price = Math.round(subscriptionPrice * 100) / 100;
  return price;
});

watch(
  () => state.subscriptionType,
  (updated) => {
    initSubscriptions(updated);
    emit('selected-sub', state.selectedSubscription);
  }
);

watch(
  () => state.selectedSubscription,
  (updated) => {
    emit('selected-sub', updated);
  }
);

onBeforeMount(() => {
  // if subs are default selected, send to PDP
  initSubscriptions(state.subscriptionType);
  emit('freq-change', state.subscriptionType);
  emit('selected-sub', state.selectedSubscription);
});

function frequencyChange(e: any) {
  state.subscriptionType = e.value;
  emit('freq-change', state.subscriptionType);
}
function subscriptionDisplay(sub: any): string {
  const subPrefs = sub?.subscription_preferences;
  const frequency = subPrefs?.charge_interval_frequency;
  const interval = subPrefs?.interval_unit;
  return sub?.type === 'subscription'
    ? `Delivered Every ${frequency > 1 ? frequency + ' ' : ''}${interval + (frequency > 1 ? 's' : '')}`
    : '';
}
function initSubscriptions(updated: string): void {
  if (updated === 'subscription') {
    (subscriptionOptions.value as any)?.receiveChange({
      name: 'subscriptionOptions',
      value: subscriptionDefault.value,
    });
    state.selectedSubscription = sortedSubscriptions.value.find(
      (subscription) => subscription.type === 'subscription' && subscription.sort_order <= 1
    );
  } else {
    state.selectedSubscription = {};
  }
}
function closeModal(): void {
  state.showLearnMore = false;
}
</script>
<style lang="scss" scoped>
.subscription-options {
  width: 100%;
  fieldset {
    @include local-mixins.solo-input-parent;

    align-items: center;
    justify-content: space-between;
    padding-top: #{local-functions.rem-calc(15)};
    padding-bottom: #{local-functions.rem-calc(15)};
    margin: 0 0 #{local-functions.rem-calc(5)};
  }
  .header {
    width: 100%;
    margin: 1.25rem 0 #{local-functions.rem-calc(5)};
    strong {
      white-space: nowrap;
    }
    .subscription-learn-more {
      text-transform: capitalize;
      font-size: 1rem;
      color: $color-primary-600;
      line-height: 1.5;
      @include local-mixins.tablet_and_mobile {
        width: auto;
      }
    }
  }
  .select-option {
    padding: #{local-functions.rem-calc(15)} 0;
  }
  .sub-section {
    display: grid;
    grid-template-areas:
      'check price'
      'select select';
    .checkradio-container {
      grid-area: check;
    }
    .options-container {
      font-size: 0.75rem;
      grid-area: select;
      width: 100%;
      margin-top: 1rem;
      border: 2px solid $color-neutral-black;
      border-radius: 4px;
      padding: 8px;
      text-transform: capitalize;
      .option {
        text-transform: capitalize;
        width: 100%;
      }
    }
  }
  .price-container {
    font-size: 0.875rem;
    grid-area: price;
    color: $color-neutral-black;
    width: auto;
    margin: 0;
    .percent-off {
      font-size: 0.75rem;
    }
    strong {
      &:nth-child(even) {
        margin-left: #{local-functions.rem-calc(5)};
      }
    }
  }
}
:deep(.modal-learn-more) {
  .modal-content {
    border-radius: 4px;
    @include local-mixins.desktop {
      width: 100%;
      max-width: calc(#{map.get(local-vars.$breakpoints, 'small-medium')});
    }
  }
  .modal-body {
    align-self: center;
    justify-self: center;
    width: 100%;
    height: 100%;
    padding: #{local-functions.rem-calc(40)};
    h2 {
      margin-bottom: 1rem;
      @include local-mixins.desktop {
        margin-bottom: 1.5rem;
      }
    }
    li {
      display: flex;
      margin: 0 0 1.25rem;
    }
    :deep(svg) {
      fill: $color-primary-400;
    }
    .copy {
      margin-left: 0.625rem;
    }
    h5 {
      line-height: 1.3;
      margin: 0;
    }
    p {
      margin: 0;
    }
  }
}
</style>
